import React, { useCallback, useEffect, useRef } from 'react';
import Card from 'react-bootstrap/Card';

import enTranslations from '../locales/en.json';
import frTranslations from '../locales/fr.json';
import { useLanguage } from './languageContext';
import {
  SKILLS,
  SKILLS_AUTO_SCROLL_INTERVAL_MS,
  SKILLS_SCROLL_ANIMATION_DURATION_MS,
} from '../constants/skills';

import '../utils/skillsCarousel.css';

const LOOPED_SKILLS = [...SKILLS, ...SKILLS];

const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

function SkillsCarousel() {
  const { language } = useLanguage();
  const translations = language === 'en' ? enTranslations : frTranslations;
  const skillsText = translations.home.skills;

  const trackRef = useRef(null);
  const timerRef = useRef(null);
  const animationFrameRef = useRef(null);

  // LOOPED_SKILLS holds the skill set twice. Once a full set has scrolled by,
  // we silently rewind by one set width: the content there is identical, so
  // the jump is invisible and the carousel feels endless.
  const wrapScrollPosition = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const oneSetWidth = track.scrollWidth / 2;
    if (track.scrollLeft >= oneSetWidth) {
      track.scrollLeft -= oneSetWidth;
    } else if (track.scrollLeft < 0) {
      track.scrollLeft += oneSetWidth;
    }
  }, []);

  const animateScrollBy = useCallback((distance, duration) => {
    const track = trackRef.current;
    if (!track) return;
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

    const startLeft = track.scrollLeft;
    const startTime = performance.now();

    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      track.scrollLeft = startLeft + distance * easeInOutQuad(progress);
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      } else {
        wrapScrollPosition();
      }
    };

    animationFrameRef.current = requestAnimationFrame(step);
  }, [wrapScrollPosition]);

  const scrollByCard = useCallback((direction) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector('.ps-skill-card');
    const step = card ? card.getBoundingClientRect().width + 24 : track.clientWidth / 3;

    if (direction < 0 && track.scrollLeft < step) {
      track.scrollLeft += track.scrollWidth / 2;
    }

    animateScrollBy(direction * step, SKILLS_SCROLL_ANIMATION_DURATION_MS);
  }, [animateScrollBy]);

  const restartAutoScroll = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => scrollByCard(1), SKILLS_AUTO_SCROLL_INTERVAL_MS);
  }, [scrollByCard]);

  useEffect(() => {
    restartAutoScroll();
    return () => {
      clearInterval(timerRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [restartAutoScroll]);

  const handleManualScroll = (direction) => {
    scrollByCard(direction);
    restartAutoScroll();
  };

  return (
    <div
      className="ps-skills-carousel"
      onMouseEnter={() => clearInterval(timerRef.current)}
      onMouseLeave={restartAutoScroll}
    >
      <button
        type="button"
        className="ps-skills-arrow ps-skills-arrow-prev"
        aria-label="previous skills"
        onClick={() => handleManualScroll(-1)}
      >
        &#8249;
      </button>

      <div className="ps-skills-track" ref={trackRef}>
        {LOOPED_SKILLS.map((skill, index) => (
          <Card key={`${skill.key}-${index}`} className="ps-skill-card">
            <div className="ps-skill-card-icon">
              {skill.Icon ? (
                <skill.Icon size={64} color={skill.color} aria-hidden="true" />
              ) : (
                <img src={skill.img} alt={`${skill.name} logo`} loading="lazy" />
              )}
            </div>
            <Card.Body>
              <Card.Title>{skill.name}</Card.Title>
              <Card.Text>{skillsText[skill.key]}</Card.Text>
            </Card.Body>
          </Card>
        ))}
      </div>

      <button
        type="button"
        className="ps-skills-arrow ps-skills-arrow-next"
        aria-label="next skills"
        onClick={() => handleManualScroll(1)}
      >
        &#8250;
      </button>
    </div>
  );
}

export default SkillsCarousel;
